import { CIO_GEO_ATTRIBUTES } from '@/utils/attributes';
import type { DataIndexItemAddress } from '../urls';
import { DATA_INDEX_ITEM_ACTIVITY_LOGS, type DataIndexItemKind } from './types';

/**
 * The identity of one row, which is all most of these questions need. Object
 * and relationship attributes carry the object type they belong to, because
 * the same attribute name means different things on different object types.
 */
export type DataIndexItemRef =
  | { id: string; kind: 'attribute' | 'event' }
  | { id: string; kind: 'object-attribute' | 'object-relationship-attribute'; object_type_id: number };

/** The activity-log type an item's changes are recorded under. */
export function getActivityLogType(item: DataIndexItemRef): string {
  if (item.kind === 'object-attribute') {
    return `_o:${String(item.object_type_id)}:attribute_change`;
  }
  if (item.kind === 'object-relationship-attribute') {
    return `_r:${String(item.object_type_id)}:attribute_change`;
  }
  return DATA_INDEX_ITEM_ACTIVITY_LOGS[item.kind];
}

export function isObjectOrRelationshipAttribute(kind: DataIndexItemKind): boolean {
  return kind === 'object-attribute' || kind === 'object-relationship-attribute';
}

/**
 * Whether the Design Studio usage surface applies. Only profile attributes
 * carry Design Studio usage; events and object/relationship attributes have
 * no such field, so showing the surface would show a permanent empty state.
 */
export function hasDesignStudioUsage(kind: DataIndexItemKind, withTemplatesSnippetsLayouts: boolean): boolean {
  return withTemplatesSnippetsLayouts && kind === 'attribute';
}

/** The noun for an item in running copy. Both flavours of object attribute are still attributes. */
export function getLabelForItem(kind: DataIndexItemKind): 'attribute' | 'event' {
  return kind === 'event' ? 'event' : 'attribute';
}

/** What can be created from an item, for the "Use in" actions. */
export type CreatableFromItem = 'campaign' | 'template' | 'newsletter' | 'segment' | 'snippet' | 'layout';

/** Whether an action is offered, and the tooltip shown when it is not. */
export type CreateAvailability = {
  content: string | undefined;
  disabled: boolean;
};

/**
 * Whether a "Use in" action is available, and the tooltip explaining why not.
 * An object or relationship attribute describes an object, so it cannot seed
 * a person-shaped record directly.
 */
export function objectOrRelationshipTooltipContent(
  kind: DataIndexItemKind,
  target: CreatableFromItem,
): CreateAvailability {
  const disabled = isObjectOrRelationshipAttribute(kind);
  const noun = kind === 'object-relationship-attribute' ? 'a relationship' : 'an object';
  return {
    content: disabled
      ? `This attribute cannot be used to create a ${target} directly as it is ${noun} attribute.`
      : undefined,
    disabled,
  };
}

/** Whether Customer.io derived this attribute from the person's IP address. */
export function isGeoIpAttribute(attributeId: string): boolean {
  return CIO_GEO_ATTRIBUTES.includes(attributeId);
}

/** How long a description someone can save. */
export const MAX_DESCRIPTION_LENGTH = 255;

/** The saved view that narrows a list to items something actually references. */
export const IN_USE_VIEW_ID = 'in-use-only';

export type DataIndexViewId = typeof IN_USE_VIEW_ID | null;

const DESIGN_STUDIO_TYPE_LABELS = new Map<string, string>([
  ['EMAIL', 'Email'],
  ['WHATSAPP', 'WhatsApp'],
  ['COMPONENT', 'Component'],
]);

/**
 * The label for a Design Studio asset's type. An unrecognised type shows its
 * raw value rather than nothing, so a type added on the backend before the UI
 * knows about it still reads as something.
 */
export function getDesignStudioTypeLabel(type: string | null | undefined): string {
  if (type == null) {
    return '';
  }
  return DESIGN_STUDIO_TYPE_LABELS.get(type) ?? type;
}

/**
 * The item an address names, as the "Use in" actions need it.
 *
 * The address carries the object type on its scope; the ref carries it on
 * the item, so the two object kinds cannot be described without one.
 */
export function itemRefFromAddress(address: DataIndexItemAddress): DataIndexItemRef {
  if (address.kind === 'object-attribute' || address.kind === 'object-relationship-attribute') {
    // Both object scopes carry a type. A profile one does not, and no
    // object kind is ever addressed with it — but defaulting to
    // `FALLBACK_OBJECT_TYPE_ID` there would mint the shape that reads as
    // a person attribute and makes records the backend rejects, so the
    // pair that combination really describes is returned instead.
    return address.scope.kind === 'profile'
      ? { id: address.name, kind: 'attribute' }
      : { id: address.name, kind: address.kind, object_type_id: address.scope.objectTypeId };
  }

  return { id: address.name, kind: address.kind };
}
